import { EVENT_FORMATS, type EventDraft } from '@project/event-format';
import type { EventCreatorState } from './state';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const optionalString = (value: unknown): boolean =>
  value === undefined || typeof value === 'string';
const optionalNumber = (value: unknown): boolean =>
  value === undefined || (typeof value === 'number' && Number.isFinite(value));
const time = (value: unknown): boolean =>
  isRecord(value) &&
  (value.kind === 'date'
    ? typeof value.date === 'string'
    : value.kind === 'dateTime' &&
      typeof value.dateTime === 'string' &&
      optionalString(value.timeZone));
const person = (value: unknown): boolean =>
  isRecord(value) && typeof value.email === 'string' && optionalString(value.name);

/** Check structure, not export validity: unfinished dates, URLs and emails must survive. */
export function isSessionDraft(value: unknown): value is EventDraft {
  if (!isRecord(value) || typeof value.title !== 'string' || !time(value.start)) return false;
  const r = value.recurrence;
  return (
    (value.end === undefined || time(value.end)) &&
    ['description', 'location', 'url', 'uid'].every((key) => optionalString(value[key])) &&
    (value.organizer === undefined || person(value.organizer)) &&
    (value.attendees === undefined ||
      (Array.isArray(value.attendees) && value.attendees.every(person))) &&
    (value.remindersMinutesBefore === undefined ||
      (Array.isArray(value.remindersMinutesBefore) &&
        value.remindersMinutesBefore.every((n) => typeof n === 'number' && Number.isFinite(n)))) &&
    (value.status === undefined ||
      ['confirmed', 'tentative', 'cancelled'].includes(String(value.status))) &&
    (value.showAs === undefined || ['busy', 'free'].includes(String(value.showAs))) &&
    (r === undefined ||
      (isRecord(r) &&
        ['daily', 'weekly', 'monthly', 'yearly'].includes(String(r.frequency)) &&
        optionalNumber(r.interval) &&
        optionalNumber(r.count) &&
        optionalString(r.until)))
  );
}

/** Restore only known state shapes; malformed or obsolete storage must not break the editor. */
export function restoreCreatorState(value: unknown): EventCreatorState | null {
  if (
    !isRecord(value) ||
    !isSessionDraft(value.draft) ||
    !EVENT_FORMATS.some((format) => format.id === value.formatId) ||
    typeof value.submitted !== 'boolean' ||
    !optionalString(value.timeZone)
  )
    return null;
  return value as unknown as EventCreatorState;
}
